import { useMemo, useState } from 'react';
import { Button, Chip, IconButton, Stack } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import { PageHeader, DataTable, ConfirmDialog, AdminPageState } from '@shared/components';
import { useApiQuery } from '@shared/hooks/useApi.js';
import { api } from '@core/api.js';
import { useNotify } from '@shared/hooks/useNotify.js';
import { formatDateTime } from '@shared/utils/formatters.js';
import { apiErrorMessage } from '@shared/utils/apiErrorMessage.js';
import { colText, colSecondary, colActions } from '@shared/utils/dataGridColumns.jsx';
import UserFormDialog from '../components/UserFormDialog.jsx';

export default function UsersPage() {
  const notify = useNotify();
  const {
    data = [], isLoading, isError, error, refetch,
  } = useApiQuery('auth-users', '/v1/auth/users');
  const [editing, setEditing] = useState(null);
  const [confirming, setConfirming] = useState(null);
  const [removing, setRemoving] = useState(false);

  const columns = useMemo(() => [
    colText('email', 'Email', { flex: 1.2, minWidth: 200, fontWeight: 600 }),
    colSecondary('displayName', 'Nom', { flex: 1, minWidth: 140 }),
    {
      field: 'roles',
      headerName: 'RÃ´les',
      flex: 1,
      minWidth: 140,
      sortable: false,
      renderCell: ({ value }) => (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          {(value || []).map((r) => <Chip key={r} label={r} size="small" />)}
        </Stack>
      ),
    },
    {
      field: 'lastLoginAt',
      headerName: 'DerniÃ¨re connexion',
      width: 170,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: ({ value }) => (value ? formatDateTime(value) : 'â€”'),
    },
    colActions(
      ({ row }) => (
        <Stack direction="row" justifyContent="flex-end">
          <IconButton size="small" aria-label="Modifier" onClick={() => setEditing(row)}>
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" aria-label="Supprimer" onClick={() => setConfirming(row)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Stack>
      ),
      { width: 100 },
    ),
  ], []);

  const remove = async () => {
    if (!confirming?.id) return;
    setRemoving(true);
    try {
      await api.delete(`/v1/auth/users/${confirming.id}`);
      notify.success('Utilisateur supprimÃ©');
      refetch();
    } catch (err) {
      notify.error(apiErrorMessage(err, 'Ã‰chec de la suppression.'));
    } finally {
      setRemoving(false);
      setConfirming(null);
    }
  };

  return (
    <>
      <PageHeader
        dense
        title="Utilisateurs"
        subtitle="Gestion des comptes et des rÃ´les"
        actions={
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => setEditing({})}>
            Nouveau
          </Button>
        }
      />
      <AdminPageState
        isLoading={isLoading}
        isError={isError}
        error={error}
        onRetry={refetch}
        isEmpty={data.length === 0}
        emptyTitle="Aucun utilisateur"
        emptyDescription="CrÃ©ez le premier compte administrateur."
      >
        <DataTable rows={data} columns={columns} loading={isLoading} density="compact" />
      </AdminPageState>
      {editing !== null && (
        <UserFormDialog
          user={editing}
          onClose={() => { setEditing(null); refetch(); }}
        />
      )}
      <ConfirmDialog
        open={!!confirming}
        title="Supprimer l'utilisateur"
        message={`ÃŠtes-vous sÃ»r de vouloir supprimer ${confirming?.email} ?`}
        destructive
        loading={removing}
        loadingLabel="Suppressionâ€¦"
        onCancel={() => setConfirming(null)}
        onConfirm={remove}
      />
    </>
  );
}
